import React, { useMemo } from "react";
import { Table, Input } from "reactstrap";

// ============================================================================
// (2026-09) Captura de cantidades por talla y color, en tabla: un renglón por
// color, una columna por talla, con totales por talla, por color y general.
//
// Props:
//   colores     [{ _id, name }]  colores a capturar (en el orden a mostrar)
//   celdas      [{ colores, tallas, tallaNombre, cantidad }]
//   onChange    (celda, valor) => void
//   precio      opcional, para mostrar el importe por color
//   ordenTallas opcional, arreglo con el orden en que van las tallas
// ============================================================================
function CapturaTallasColores({
  colores = [],
  celdas = [],
  onChange,
  precio = 0,
  ordenTallas = [],
}) {
  const id = (v) => String(v?._id || v || "");
  const n = (v) => Number(v || 0) || 0;
  const claveCelda = (c) => `${id(c?.tallas)}||${String(c?.tallaNombre || "")}`;

  // Columnas: las tallas que existan en las celdas, sin repetir
  const columnas = useMemo(() => {
    const m = new Map();
    (celdas || []).forEach((c) => {
      const k = claveCelda(c);
      if (!m.has(k)) {
        m.set(k, { clave: k, nombre: String(c?.tallaNombre || "").trim() });
      }
    });

    const pos = (nombre) => {
      // "26 LP +4" ordena junto a la 26: el índice sale de la talla base
      const base = String(nombre || "").split(" ")[0].toUpperCase();
      const i = ordenTallas.findIndex((t) => String(t).toUpperCase() === base);
      return i === -1 ? Infinity : i;
    };

    return [...m.values()].sort(
      (a, b) =>
        pos(a.nombre) - pos(b.nombre) ||
        a.nombre.localeCompare(b.nombre, "es", { numeric: true })
    );
  }, [celdas, ordenTallas]);

  // Índice color -> (talla -> celda), para pintar cada cuadro
  const porColor = useMemo(() => {
    const m = new Map();
    (celdas || []).forEach((c) => {
      const kc = id(c?.colores);
      if (!m.has(kc)) m.set(kc, new Map());
      m.get(kc).set(claveCelda(c), c);
    });
    return m;
  }, [celdas]);

  const totalPorColumna = (clave) =>
    (celdas || [])
      .filter((c) => claveCelda(c) === clave)
      .reduce((s, c) => s + n(c.cantidad), 0);

  const totalPorColor = (colorId) =>
    (celdas || [])
      .filter((c) => id(c.colores) === String(colorId))
      .reduce((s, c) => s + n(c.cantidad), 0);

  const granTotal = (celdas || []).reduce((s, c) => s + n(c.cantidad), 0);

  const money = (v) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 2,
    }).format(n(v));

  if (!colores.length || !columnas.length) {
    return (
      <p className="text-muted mb-2">
        Selecciona el artículo, sus colores y sus tallas para capturar
        cantidades.
      </p>
    );
  }

  return (
    <div style={{ overflowX: "auto" }}>
      <Table size="sm" bordered className="mb-2 align-middle">
        <thead>
          <tr className="text-center">
            <th style={{ minWidth: 130, textAlign: "left" }}>COLOR</th>
            {columnas.map((c) => (
              <th key={c.clave} style={{ minWidth: 58 }}>
                {c.nombre}
              </th>
            ))}
            <th style={{ minWidth: 70 }}>TOTAL</th>
            {n(precio) > 0 && <th style={{ minWidth: 90 }}>IMPORTE</th>}
          </tr>
        </thead>

        <tbody>
          {colores.map((col) => {
            const colorId = id(col);
            const fila = porColor.get(colorId) || new Map();
            const totalColor = totalPorColor(colorId);

            return (
              <tr key={colorId}>
                <td>
                  <strong>{col.name || col.nombre}</strong>
                </td>

                {columnas.map((c) => {
                  const celda = fila.get(c.clave);
                  if (!celda) {
                    return (
                      <td
                        key={c.clave}
                        className="text-center text-muted"
                        style={{ background: "#f8f9fa" }}
                      >
                        —
                      </td>
                    );
                  }
                  return (
                    <td key={c.clave} className="p-1">
                      <Input
                        bsSize="sm"
                        type="number"
                        min="0"
                        value={celda.cantidad}
                        onChange={(e) => onChange(celda, e.target.value)}
                        className="text-center"
                        style={{ padding: "2px 4px" }}
                      />
                    </td>
                  );
                })}

                <td className="text-center">
                  <strong>{totalColor}</strong>
                </td>

                {n(precio) > 0 && (
                  <td className="text-end">{money(totalColor * n(precio))}</td>
                )}
              </tr>
            );
          })}
        </tbody>

        <tfoot>
          <tr className="text-center" style={{ background: "#f1f3f5" }}>
            <th style={{ textAlign: "left" }}>TOTAL</th>
            {columnas.map((c) => (
              <th key={c.clave}>{totalPorColumna(c.clave) || ""}</th>
            ))}
            <th>{granTotal}</th>
            {n(precio) > 0 && (
              <th className="text-end">{money(granTotal * n(precio))}</th>
            )}
          </tr>
        </tfoot>
      </Table>
    </div>
  );
}

export default CapturaTallasColores;
